import { IcoPlusSmall16, IcoSparkles16 } from '@onefootprint/icons';
import type { AiResearchTask, ApiNodeDataAiResearch } from '@onefootprint/request-types/dashboard';
import { Form, LinkButton, Tabs, type TemplateInputProps } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import { useGetNodeConfigContext } from '@/components/playbook-builder/hooks/use-get-node-config-context';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { ApplyEditMutation } from '../../base-node';
type AiResearchNodeFormProps = {
  node: NodeProps<ApiNodeDataAiResearch>;
  mutation: ApplyEditMutation;
  canEdit: boolean;
};
type AiResearchFormData = {
  name?: string;
  additionalSystemInstructions?: string;
  friendlyDescription?: string;
  tasks?: Array<AiResearchTask>;
};
const getDefaultValues = (tasks: AiResearchTask[]) => {
  if (!tasks || tasks.length === 0) {
    return [{
      name: '',
      objective: '',
      onboardingDataKey: ''
    }];
  }
  return tasks.map(task => ({
    name: task.name,
    objective: task.objective,
    onboardingDataKey: task.onboardingDataKey
  }));
};
export const AiResearchNodeForm = ({
  node,
  mutation,
  canEdit
}: AiResearchNodeFormProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'aiResearchNode'
  });
  const {
    toggle
  } = useDirty();
  const [activeTab, setActiveTab] = useState<'basic' | 'advanced'>('basic');
  const form = useForm<AiResearchFormData>({
    disabled: !canEdit,
    defaultValues: {
      name: node.data.name || '',
      additionalSystemInstructions: node.data.additionalSystemInstructions || '',
      friendlyDescription: node.data.friendlyDescription || '',
      tasks: getDefaultValues(node.data.tasks)
    }
  });
  const config = useGetNodeConfigContext(node.id, 'templatable');
  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control: form.control,
    name: 'tasks'
  });
  useEffect(() => {
    toggle('ai-research-form', form.formState.isDirty);
  }, [form.formState.isDirty]);
  const onAddTask = () => {
    append({
      name: '',
      objective: '',
      onboardingDataKey: ''
    }, {
      shouldFocus: false
    });
  };
  const handleFormSubmit = async (data: AiResearchFormData) => {
    try {
      await mutation.mutateAsync({
        nodeId: node.id,
        kind: 'update_node',
        nodeData: {
          kind: 'ai_research',
          name: data.name?.trim(),
          additionalSystemInstructions: data.additionalSystemInstructions?.trim(),
          friendlyDescription: data.friendlyDescription?.trim(),
          tasks: data.tasks ?? []
        }
      });
    } catch (error) {
      console.error('Error updating playbook session:', error);
    }
  };
  const autocompleteItems = (() => {
    return Object.entries(config?.autocomplete || {}).reduce((acc, [key, value]) => {
      acc[key === 'workflowContextKeys' ? 'others' : key] = value.map(item => ({
        label: item.display,
        value: item.token,
        detail: item.dataType.join(' | ')
      }));
      return acc;
    }, {} as TemplateInputProps['autocompleteItems']);
  })();
  return <FormProvider {...form}>
      <form id="ai-research-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="flex flex-col gap-6">
        <Tabs options={[{
        label: t($ => $.tabs.basic),
        value: 'basic'
      }, {
        label: t($_0 => $_0.tabs.advanced),
        value: 'advanced'
      }]} onChange={val => setActiveTab(val as 'basic' | 'advanced')} defaultValue={activeTab} />

        {activeTab === 'basic' && <div className="flex flex-col gap-6">
            <div className="flex flex-col p-3 gap-2 border border-solid border-tertiary rounded-md">
              <div className="flex gap-1">
                <IcoSparkles16 />
                <p className="text-label-2">{t($_1 => $_1.whatWillPercyDo)}</p>
              </div>
              <p className="text-body-2 text-secondary">{form.getValues('friendlyDescription')}</p>
            </div>
            <Form.Textarea<AiResearchFormData> disabled={!canEdit} label={t($_2 => $_2.additionalInstructionsLabel)} name="additionalSystemInstructions" placeholder={t($_3 => $_3.additionalInstructionsPlaceholder)} />
          </div>}

        {activeTab === 'advanced' && <div className="flex flex-col gap-6 p-4 border border-tertiary rounded">
            <div className="flex flex-col rounded-md">
              <div className="flex flex-col rounded-md mb-5 gap-4">
                <Form.Input label={t($_4 => $_4.nameLabel)} name="name" placeholder={t($_5 => $_5.namePlaceholder)} size="compact" />
                <Form.Textarea label={t($_6 => $_6.friendlyDescriptionLabel)} name="friendlyDescription" placeholder={t($_7 => $_7.friendlyDescriptionPlaceholder)} size="compact" />
                <Form.Textarea label={t($_8 => $_8.additionalInstructionsLabel)} name="additionalSystemInstructions" placeholder={t($_9 => $_9.additionalInstructionsPlaceholder)} size="compact" />
              </div>

              <div className="flex flex-col gap-6">
                {fields.map((field, index) => {
              return <section key={field.id} className="flex flex-col border border-tertiary rounded p-4">
                      <div className="flex flex-col gap-6">
                        <div className="flex justify-between items-center">
                          <div className="flex flex-col gap-2">
                            <label htmlFor="task-name" className="text-body-2">
                              {t($_10 => $_10.taskObjectiveLabel)}
                            </label>
                            <p className="text-caption-2 text-tertiary">{t($_11 => $_11.taskObjectiveSubtitle)}</p>
                          </div>
                          {fields.length > 1 && <LinkButton destructive type="button" onClick={() => remove(index)} disabled={!canEdit} className="flex-shrink-0">
                              {t($_12 => $_12.deleteTask)}
                            </LinkButton>}
                        </div>

                        <div className="flex flex-col gap-4">
                          <Form.TemplateTextarea name={`tasks.${index}.objective`} autocompleteItems={autocompleteItems} placeholder={t($_13 => $_13.taskObjectivePlaceholder)} size="compact" autoFocus rules={{
                      required: t($_14 => $_14.taskObjectiveRequired)
                    }} errorPosition="absolute" />
                        </div>

                        <div className="flex flex-col gap-6 mt-2">
                          <Form.Input name={`tasks.${index}.onboardingDataKey`} label={t($_15 => $_15.taskDataKeyLabel)} placeholder={t($_16 => $_16.taskDataKeyPlaceholder)} size="compact" rules={{
                      required: true
                    }} addon="data." />
                        </div>
                      </div>
                    </section>;
            })}
              </div>
            </div>

            <LinkButton type="button" onClick={onAddTask} iconComponent={IcoPlusSmall16} iconPosition="left" disabled={!canEdit}>
              {t($_17 => $_17.addTask)}
            </LinkButton>
          </div>}
      </form>
    </FormProvider>;
};